import { Check, Copy, Globe, Shield } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ProjectDto } from '@ederest/contracts';
import { Badge } from '../../shared/ui/Badge.js';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import { PageHeader } from '../../shared/layout/PageHeader.js';
import { Table, type Column } from '../../shared/ui/Table.js';
import { useActiveProject } from '../../shared/api/useActiveProject.js';
import './projects.css';

export function ProjectsPage() {
  const { projects, project, select } = useActiveProject();

  const columns: Array<Column<ProjectDto>> = [
    {
      key: 'name',
      header: 'Projet',
      render: (row) => (
        <div className="cell-primary">
          <span className="cell-primary__icon">
            <Globe size={14} />
          </span>
          <div>
            <div className="cell-primary__label">{row.name}</div>
            <div className="cell-primary__sub">{row.primaryUrl ?? '—'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'languages',
      header: 'Langues',
      render: (row) => (
        <div className="project__langs">
          {row.activeLanguages.map((code) => (
            <Badge key={code}>{code.toUpperCase()}</Badge>
          ))}
        </div>
      ),
    },
    { key: 'key', header: 'Clé publique', render: (row) => <CopyKey value={row.publicKey} /> },
    {
      key: 'active',
      header: '',
      align: 'end',
      render: (row) =>
        row.id === project?.id ? (
          <Badge tone="brand">Projet actif</Badge>
        ) : (
          <Button variant="ghost" onClick={() => select(row.id)}>
            Activer
          </Button>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Projets"
        description="Chaque projet est cloisonné dès la collecte. La même ligne d'appel, le même script, pour tous."
      />

      <Card padded={false}>
        <Table columns={columns} rows={projects} rowKey={(row) => row.id} empty="Aucun projet raccordé." />
      </Card>

      {project && (
        <Card title="Mise en place de la mesure" icon={<Shield size={15} />} className="project__snippet">
          <p className="project__snippet-hint">
            La balise d'intégration du projet actif, avec son empreinte d'intégrité, l'interrupteur de
            suspension et les versions de configuration se trouvent dans l'écran{' '}
            <Link to="/script" className="project__link">
              Administration du script
            </Link>
            . En démonstration, l'Extension Ederest injecte <strong>exactement le même fichier</strong> depuis
            un poste interne — aucune intégration n'est demandée au client.
          </p>
        </Card>
      )}
    </>
  );
}

function CopyKey({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      className="project__key"
      onClick={() => {
        void navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
    >
      <code>{value.slice(0, 12)}…</code>
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}
